import i18n from '@/lib/i18n';

/**
 * Plain-language copy for WhatsApp channel error codes (backend 98xxx block,
 * SP1 design spec §5).
 *
 * Same doctrine as `voice-error-copy.ts`: the backend returns a code plus
 * safe context and never writes end-user prose, so every friendly sentence
 * lives here — reusing the registry's own message text
 * (`error-code.registry.ts`) as the English default.
 *
 * The English strings are i18n *defaults*: `whatsappErrorMessage()` resolves
 * `whatsapp.errors.<code>` and falls back to them, so an untranslated locale
 * renders English rather than a raw key.
 *
 * Wayfinding in 98000/98001 names the SIDEBAR entry (Settings → WhatsApp),
 * not the URL, which is still `/settings/channels/whatsapp`. WhatsApp got its
 * own settings nav item, so 'Settings → Channels' would now send people to the
 * channel list to hunt for a card. SMS-number errors elsewhere still say
 * 'Settings → Channels' — that is correct for them and must stay.
 */
/** `WHATSAPP_TEMPLATE_SUBMIT_FAILED` — the one code that carries Meta's own reason. */
const SUBMIT_FAILED_CODE = 98009;

/**
 * Meta's 131042 — "Business eligibility payment issue".
 *
 * Classified as a plain send failure by `classifyMetaError`, so it arrives as
 * 98004 and used to read "Message couldn't be delivered." — which sends the
 * operator hunting through the composer for a bug that is a billing problem
 * on the WhatsApp Business account. The Meta code rides along in
 * `details.metaCode`, so this is answerable here without a new error code.
 */
const META_BILLING_CODE = 131042;

const COPY: Record<number, string> = {
    98000: 'WhatsApp is not connected for your organization. Connect it in Settings → WhatsApp.',
    98001: 'We could not connect WhatsApp. Check the connection in Settings → WhatsApp, then try again.',
    98002: "This contact hasn't opted in to WhatsApp messages.",
    98003: 'Use an approved WhatsApp template to start this message.',
    98004: "Message couldn't be delivered.",
    98005: "This file type can't be sent on WhatsApp.",
    98006: "This WhatsApp number isn't available right now.",
    98007: "This WhatsApp template doesn't exist.",
    98008: "This template isn't approved yet.",
    98009: "We couldn't submit this template for review.",
    98010: 'A template with this name and language already exists.',
    98011: "This template can't be edited in its current status.",
};

interface IApiErrorEnvelope {
    readonly error?:
        | {
              readonly errorCode?: number;
              readonly details?: {
                  /** Meta's own wording for a submit rejection, when it sent any. */
                  readonly metaReason?: string | null;
                  /** Meta's raw error code, passed through by `mapWhatsAppSendError`. */
                  readonly metaCode?: number | null;
              } | null;
          }
        | string;
}

/**
 * Friendly copy for a WhatsApp error response, or `undefined` when the
 * payload is not one of ours — callers then fall through to their own
 * fallback, the same contract as `voiceErrorMessage`.
 */
export function whatsappErrorMessage(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const { error } = data as IApiErrorEnvelope;
    if (!error || typeof error !== 'object') return undefined;

    const { errorCode } = error;
    if (typeof errorCode !== 'number') return undefined;

    const fallback = COPY[errorCode];
    if (!fallback) return undefined;

    // Meta's own code outranks our classification when it names something the
    // operator can actually act on — our 98004 bucket cannot.
    if (error.details?.metaCode === META_BILLING_CODE) {
        return i18n.t(
            'whatsapp.errors.meta131042',
            'WhatsApp rejected this: the business account has a billing problem. Add or fix the payment method in Meta Business Manager, then try again.',
        );
    }

    const generic = i18n.t(`whatsapp.errors.${String(errorCode)}`, fallback);

    // A submit rejection is the one case where the backend DOES have prose
    // worth reading: Meta's own explanation of what it objected to. The
    // doctrine above ("the backend never writes end-user prose") holds for our
    // own errors — this text is Meta's, it is the only actionable thing in the
    // response, and without it every rejection reads identically.
    const metaReason = error.details?.metaReason;
    if (errorCode === SUBMIT_FAILED_CODE && typeof metaReason === 'string' && metaReason.trim()) {
        return `${generic} ${metaReason.trim()}`;
    }

    return generic;
}
